import React, { useEffect, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { RootState } from '../store/reducers';
import { fetchDecisionsRequest, fetchStatsRequest } from '../store/decisions/actions';
import { fetchUserStats } from '../store/users/service';
import { UserStats } from '../store/users/reducer';
import { FileText, Sparkles, FileCheck, Download, ArrowRight, Calendar, TrendingUp, Briefcase, CheckCircle, XCircle, Edit3 } from 'lucide-react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell } from 'recharts';
import './Dashboard.css';

const statutLabels: Record<string, { label: string; color: string; bg: string }> = {
  brouillon:  { label: 'Brouillon',   color: '#6B7280', bg: '#F3F4F6' },
  a_verifier: { label: 'À vérifier',  color: '#C4A535', bg: '#FFF8E7' },
  valide:     { label: 'Validé',       color: '#047857', bg: '#ECFDF5' },
  publie:     { label: 'Publié',       color: '#059669', bg: '#D1FAE5' },
  rejete:     { label: 'Rejeté',       color: '#DC2626', bg: '#FEF2F2' },
};

const JURIDICTION_COLORS = [
  '#1B2A4A', '#2563EB', '#059669', '#7C3AED', '#DB2777',
  '#C4A535', '#0891B2', '#4F46E5', '#65A30D', '#DC2626',
];

const Dashboard: React.FC = () => {
  const { user } = useSelector((state: RootState) => state.auth);
  const { decisions, count, isLoading, stats, statsLoading } = useSelector(
    (state: RootState) => state.decisions
  );
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [myStats, setMyStats] = useState<UserStats | null>(null);

  useEffect(() => {
    dispatch(fetchDecisionsRequest({ page: 1, page_size: 10 }));
    dispatch(fetchStatsRequest());
  }, [dispatch]);

  useEffect(() => {
    if (user?.id) {
      fetchUserStats(user.id).then(res => setMyStats(res.data)).catch(() => {});
    }
  }, [user?.id]);

  const displayName = user?.first_name
    ? `${user.first_name} ${user.last_name || ''}`
    : user?.email || 'Agent';

  // Stats from API or fallback to local computation
  const parStatut = stats?.par_statut || {};
  const totalDecisions = stats ? Object.values(parStatut).reduce((a, b) => a + b, 0) : count;
  const pendingCount = parStatut['a_verifier'] || 0;
  const validatedCount = (parStatut['valide'] || 0);
  const publishedCount = parStatut['publie'] || 0;

  // Bar chart data from API
  const volumeData = (stats?.volume_mensuel || []).map(item => ({
    mois: item.mois,
    valeur: item.total,
  }));

  // Pie chart data from API
  const juridictionData = (stats?.par_juridiction || []).map((item, index) => ({
    name: item.juridiction,
    value: item.total,
    color: JURIDICTION_COLORS[index % JURIDICTION_COLORS.length],
  }));

  const getAgentName = (agent: any) => {
    if (!agent) return '—';
    if (agent.first_name || agent.last_name) {
      return `${agent.first_name || ''} ${agent.last_name || ''}`.trim();
    }
    return agent.email || '—';
  };

  return (
    <div className="dashboard">
      {/* Welcome section */}
      <div className="dashboard-welcome">
        <div className="dashboard-welcome-text">
          <h2>Bonjour, {displayName}</h2>
          <p>Bienvenue dans votre espace de travail. Voici un résumé de l'activité.</p>
        </div>
        <div className="dashboard-welcome-actions">
          <button className="btn-primary" onClick={() => navigate('/espace-tca/decisions')}>
            Voir les décisions <ArrowRight size={16} />
          </button>
          <button className="btn-secondary" onClick={() => navigate('/espace-tca/numerisation')}>
            Importer
          </button>
        </div>
      </div>

      {/* Personal stats */}
      {myStats && (
        <div className="dashboard-stats">
          <div className="stat-card">
            <div className="stat-card-icon"><FileText size={20} /></div>
            <div className="stat-card-info">
              <span className="stat-card-label">Décisions assignées</span>
              <span className="stat-card-value">{myStats.decisions_count}</span>
            </div>
          </div>
          <div className="stat-card">
            <div className="stat-card-icon"><Briefcase size={20} /></div>
            <div className="stat-card-info">
              <span className="stat-card-label">En charge</span>
              <span className="stat-card-value">{myStats.charge}</span>
            </div>
          </div>
          <div className="stat-card">
            <div className="stat-card-icon"><CheckCircle size={20} /></div>
            <div className="stat-card-info">
              <span className="stat-card-label">Traitées</span>
              <span className="stat-card-value">{myStats.nombre_traitees}</span>
            </div>
          </div>
          <div className="stat-card">
            <div className="stat-card-icon"><Download size={20} /></div>
            <div className="stat-card-info">
              <span className="stat-card-label">Publiées</span>
              <span className="stat-card-value">{myStats.publiees}</span>
            </div>
          </div>
          <div className="stat-card">
            <div className="stat-card-icon"><Edit3 size={20} /></div>
            <div className="stat-card-info">
              <span className="stat-card-label">En brouillon</span>
              <span className="stat-card-value">{myStats.en_brouillon}</span>
            </div>
          </div>
          <div className="stat-card">
            <div className="stat-card-icon"><XCircle size={20} /></div>
            <div className="stat-card-info">
              <span className="stat-card-label">Rejetées</span>
              <span className="stat-card-value">{myStats.rejetees}</span>
            </div>
          </div>
        </div>
      )}

      {/* Global stats cards */}
      <div className="dashboard-stats">
        <div className="stat-card">
          <div className="stat-card-icon"><FileText size={20} /></div>
          <div className="stat-card-info">
            <span className="stat-card-label">Décisions totales</span>
            <span className="stat-card-value">{totalDecisions}</span>
          </div>
        </div>
        <div className="stat-card">
          <div className="stat-card-icon"><Sparkles size={20} /></div>
          <div className="stat-card-info">
            <span className="stat-card-label">À vérifier</span>
            <span className="stat-card-value">{pendingCount}</span>
          </div>
        </div>
        <div className="stat-card">
          <div className="stat-card-icon"><FileCheck size={20} /></div>
          <div className="stat-card-info">
            <span className="stat-card-label">Validées</span>
            <span className="stat-card-value">{validatedCount}</span>
          </div>
        </div>
        <div className="stat-card">
          <div className="stat-card-icon"><Download size={20} /></div>
          <div className="stat-card-info">
            <span className="stat-card-label">Publiées</span>
            <span className="stat-card-value">{publishedCount}</span>
          </div>
        </div>
        {stats && (
          <>
            <div className="stat-card">
              <div className="stat-card-icon"><Calendar size={20} /></div>
              <div className="stat-card-info">
                <span className="stat-card-label">Ce mois</span>
                <span className="stat-card-value">{stats.ce_mois}</span>
              </div>
            </div>
            <div className="stat-card">
              <div className="stat-card-icon"><TrendingUp size={20} /></div>
              <div className="stat-card-info">
                <span className="stat-card-label">Cette semaine</span>
                <span className="stat-card-value">{stats.cette_semaine}</span>
              </div>
            </div>
          </>
        )}
      </div>

      {/* Charts row */}
      <div className="dashboard-charts">
        <div className="chart-card">
          <h3 className="chart-title">Volume mensuel des décisions</h3>
          {statsLoading ? (
            <div className="chart-loading">Chargement...</div>
          ) : volumeData.length > 0 ? (
            <ResponsiveContainer width="100%" height={250}>
              <BarChart data={volumeData}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                <XAxis dataKey="mois" axisLine={false} tickLine={false} fontSize={12} />
                <YAxis axisLine={false} tickLine={false} fontSize={12} />
                <Tooltip />
                <Bar dataKey="valeur" fill="#1B2A4A" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          ) : (
            <div className="chart-empty">Aucune donnée disponible</div>
          )}
        </div>

        <div className="chart-card">
          <h3 className="chart-title">Répartition par juridiction</h3>
          {statsLoading ? (
            <div className="chart-loading">Chargement...</div>
          ) : juridictionData.length > 0 ? (
            <div className="donut-container">
              <ResponsiveContainer width="100%" height={200}>
                <PieChart>
                  <Pie
                    data={juridictionData}
                    cx="50%"
                    cy="50%"
                    innerRadius={55}
                    outerRadius={85}
                    paddingAngle={3}
                    dataKey="value"
                  >
                    {juridictionData.map((entry, index) => (
                      <Cell key={index} fill={entry.color} />
                    ))}
                  </Pie>
                  <Tooltip />
                </PieChart>
              </ResponsiveContainer>
              <div className="donut-legend">
                {juridictionData.map((item, index) => (
                  <div key={index} className="donut-legend-item">
                    <span className="donut-legend-dot" style={{ background: item.color }} />
                    <span className="donut-legend-label">{item.name}</span>
                    <span className="donut-legend-value">{item.value}</span>
                  </div>
                ))}
              </div>
            </div>
          ) : (
            <div className="chart-empty">Aucune donnée disponible</div>
          )}
        </div>
      </div>

      {/* File de traitement */}
      <div className="dashboard-queue">
        <div className="queue-header">
          <h3 className="chart-title">Décisions récentes</h3>
          <button className="btn-link" onClick={() => navigate('/espace-tca/decisions')}>
            Voir tout <ArrowRight size={14} />
          </button>
        </div>
        {isLoading ? (
          <p style={{ textAlign: 'center', color: '#999', padding: '20px' }}>Chargement...</p>
        ) : decisions.length > 0 ? (
          <table className="queue-table">
            <thead>
              <tr>
                <th>N° Décision</th>
                <th>Juridiction</th>
                <th>Date</th>
                <th>Statut</th>
                <th>Agent</th>
              </tr>
            </thead>
            <tbody>
              {decisions.filter(d => d.statut !== 'brouillon' && d.date_decision).slice(0, 5).map((d) => {
                const s = statutLabels[d.statut] || { label: d.statut, color: '#666', bg: '#f3f4f6' };
                return (
                  <tr key={d.id} onClick={() => navigate(`/espace-tca/decisions/${d.id}`)} className="queue-row-clickable">
                    <td className="queue-numero">{d.numero || '—'}</td>
                    <td>{d.juridiction || '—'}</td>
                    <td>{d.date_decision ? new Date(d.date_decision).toLocaleDateString('fr-FR') : '—'}</td>
                    <td>
                      <span className="status-badge" style={{ color: s.color, background: s.bg }}>
                        {s.label}
                      </span>
                    </td>
                    <td>{getAgentName(d.agent)}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        ) : (
          <p style={{ textAlign: 'center', color: '#999', padding: '20px' }}>Aucune décision.</p>
        )}
      </div>
    </div>
  );
};

export default Dashboard;
